import createReducer from 'utils/createReducer';
import move from 'utils/move';

const SET_STOPS = 'routing/SET_STOPS';
const SET_MOVE_STOP = 'routing/SET_MOVE_STOP';
const SET_REMOVE_STOP = 'routing/SET_REMOVE_STOP';
const SET_ROUTE_PLAN = 'routing/SET_ROUTE_PLAN';
const SET_ADD_ITINERARY_ID = 'routing/SET_ADD_ITINERARY_ID';
const SET_JOURNEY_FOR_UPGRADE = 'routing/SET_JOURNEY_FOR_UPGRADE';
const SET_UI = 'routing/SET_UI';
const SET_ERROR = 'routing/SET_ERROR';
const SET_CLEAR = 'routing/SET_CLEAR';
const REMOVE_ITINERARY_INCLUDING_STOP_ID = 'routing/REMOVE_ITINERARY_INCLUDING_STOP_ID';
const SET_MODIFIED_COST = 'routing/SET_MODIFIED_COST';
const SET_EDIT_STOP = 'routing/SET_EDIT_STOP';
const SET_MAP_STOP_FILTER = 'routing/SET_MAP_STOP_FILTER';

const INIT_STATE = {
  stops: [],
  /**
   * [itineraryId]: [...stopIds]
   */
  itineraryIds: {},
  routePlan: null,
  journeyForUpgrade: null,
  error: null,
  ui: {
    fetching: false,
    creating: false,
    mapOpened: false,
    hoveredStop: null,
  },
  modifiedCost: undefined,
  mapStopFilter: ['PICKUP'],
};

const reducer = createReducer(INIT_STATE, {
  [SET_CLEAR]() {
    return INIT_STATE;
  },
  [SET_ADD_ITINERARY_ID](state, { itineraryId, stopIds }) {
    return {
      ...state,
      itineraryIds: {
        ...state.itineraryIds,
        [itineraryId]: stopIds,
      },
    };
  },
  [SET_JOURNEY_FOR_UPGRADE](state, { journey }) {
    return {
      ...state,
      journeyForUpgrade: journey,
    };
  },
  [SET_STOPS](state, { stops }) {
    return { ...state, stops };
  },
  [SET_MOVE_STOP](state, { fromIndex, toIndex }) {
    return {
      ...state,
      stops: move(state.stops, fromIndex, toIndex),
    };
  },
  [SET_EDIT_STOP](state, { index, values }) {
    const currentStops = state.stops;

    const stopToModify = currentStops[index];

    const newStop = { ...stopToModify, ...values };

    return {
      ...state,
      stops: [...currentStops.slice(0, index), newStop, ...currentStops.slice(index + 1)],
    };
  },
  [SET_REMOVE_STOP](state, { index }) {
    const currentStops = state.stops;

    const stopToBeRemoved = currentStops[index];
    const shipmentsToBeRemoved = stopToBeRemoved?.shipment_ids || [];
    const hasShipments = shipmentsToBeRemoved.length;

    const stopsToBeRemovedByIndex = hasShipments
      ? currentStops.reduce((acc, stop, idx) => {
          const shipmentsMatching = shipmentsToBeRemoved.every((shipmentId) =>
            stop.shipment_ids.includes(shipmentId)
          );
          if (shipmentsMatching) {
            return [...acc, idx];
          }
          return acc;
        }, [])
      : [index];

    const newStops = currentStops.filter((_, idx) => !stopsToBeRemovedByIndex.includes(idx));

    if (!newStops?.length) {
      return INIT_STATE;
    }

    return {
      ...state,
      stops: newStops,
    };
  },
  [SET_ROUTE_PLAN](state, { routePlan }) {
    return {
      ...state,
      routePlan,
    };
  },
  [SET_UI](state, { key, value }) {
    return {
      ...state,
      ui: { ...state.ui, [key]: value },
    };
  },
  [SET_ERROR](state, { error }) {
    return {
      ...state,
      error,
    };
  },
  [REMOVE_ITINERARY_INCLUDING_STOP_ID](state, { stopId: stopIdToRemove }) {
    const currentItineraryIds = state.itineraryIds;

    const newItineraryIds = Object.keys(currentItineraryIds).reduce((acc, itineraryId) => {
      const newStopIds = currentItineraryIds[itineraryId].filter(
        (stopId) => stopId !== stopIdToRemove
      );
      if (!newStopIds?.length) {
        return { ...acc };
      }
      return {
        ...acc,
        [itineraryId]: newStopIds,
      };
    }, {});

    const emptyItineraries = !Object.keys(newItineraryIds).length;

    if (emptyItineraries) {
      return INIT_STATE;
    }

    return {
      ...state,
      itineraryIds: newItineraryIds,
    };
  },
  [SET_MODIFIED_COST](state, { modifiedCost }) {
    return {
      ...state,
      modifiedCost: modifiedCost,
    };
  },
  [SET_MAP_STOP_FILTER](state, { mapStopFilter: mapStopFilterNewValue }) {
    const currentMapStopFilter = state.mapStopFilter;
    const newMapStopFilter = currentMapStopFilter.includes(mapStopFilterNewValue)
      ? currentMapStopFilter.filter((filterValue) => filterValue !== mapStopFilterNewValue)
      : [...currentMapStopFilter, mapStopFilterNewValue];

    return {
      ...state,
      mapStopFilter: newMapStopFilter,
    };
  },
});

const setClear = () => ({
  type: SET_CLEAR,
  payload: {},
});

const setStops = (stops) => ({
  type: SET_STOPS,
  payload: { stops },
});

const setMoveStop = (fromIndex, toIndex) => ({
  type: SET_MOVE_STOP,
  payload: { fromIndex, toIndex },
});

const setRemoveStop = (index) => ({
  type: SET_REMOVE_STOP,
  payload: { index },
});

const setRoutePlan = (routePlan) => ({
  type: SET_ROUTE_PLAN,
  payload: { routePlan },
});

const setAddItineraryId = (itineraryId, stopIds) => ({
  type: SET_ADD_ITINERARY_ID,
  payload: { itineraryId, stopIds },
});

const removeItineraryIncludingStopId = (stopId) => ({
  type: REMOVE_ITINERARY_INCLUDING_STOP_ID,
  payload: { stopId },
});

const setUI = (key, value) => ({
  type: SET_UI,
  payload: { key, value },
});

const setJourneyForUpgrade = (journey) => ({
  type: SET_JOURNEY_FOR_UPGRADE,
  payload: { journey },
});

const setError = (error) => ({
  type: SET_ERROR,
  payload: { error },
});

const setModifiedCost = (modifiedCost) => ({
  type: SET_MODIFIED_COST,
  payload: { modifiedCost },
});

const setEditStop = (index, values) => ({
  type: SET_EDIT_STOP,
  payload: { index, values },
});

const setMapStopFilter = (mapStopFilter) => ({
  type: SET_MAP_STOP_FILTER,
  payload: { mapStopFilter },
});

export default reducer;
export {
  INIT_STATE,
  setClear,
  setStops,
  setMoveStop,
  setRemoveStop,
  setRoutePlan,
  setAddItineraryId,
  removeItineraryIncludingStopId,
  setJourneyForUpgrade,
  setUI,
  setError,
  setModifiedCost,
  setEditStop,
  setMapStopFilter,
};
